import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import GlLinesDialog from "@/components/GlLinesDialog";

const inr = (v) => `₹${Math.round(v || 0).toLocaleString("en-IN")}`;
const HEAD_COLORS = { revenue: "text-slate-800", igst: "text-slate-700", cgst: "text-slate-700", sgst: "text-slate-700", itc_igst: "text-slate-700", itc_cgst: "text-slate-700", itc_sgst: "text-slate-700" };

export const GstGlRecon = ({ side = "output" }) => {
  const retLabel = side === "input" ? "GSTR-2" : "GSTR-1";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [building, setBuilding] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showAccts, setShowAccts] = useState(false);
  const [drill, setDrill] = useState(null);
  const pollRef = useRef(0);
  const fellBack = useRef(false);

  const fetchOnce = (f, t, refresh) => {
    const myId = ++pollRef.current;
    api.get("/reports/gst-recon", { params: { from_month: f, to_month: t, side, ...(refresh ? { refresh: "1" } : {}) } })
      .then(({ data }) => {
        if (myId !== pollRef.current) return;
        if (data.building) {
          setBuilding(data.message || "Reconciling against the SAP trial balance…");
          setTimeout(() => { if (myId === pollRef.current) fetchOnce(f, t, false); }, 5000);
        } else {
          const empty = (data.rows || []).length > 0 && data.rows.every((r) => !r.gstr1 && !r.gl);
          if (empty && f && !fellBack.current) {
            fellBack.current = true;  // nothing this month yet — widen to all months
            setFrom(""); setTo("");
            fetchOnce("", "", false);
            return;
          }
          setBuilding(null);
          setData(data);
        }
      })
      .catch((e) => {
        if (myId !== pollRef.current) return;
        setBuilding(null);
        setError(e.response?.data?.detail || e.message);
      });
  };

  const load = (f = from, t = to, refresh = false) => {
    setData(null);
    setError("");
    setBuilding(null);
    fetchOnce(f, t, refresh);
  };
  useEffect(() => {
    const d = new Date();
    const cur = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    fellBack.current = false;
    setFrom(cur); setTo(cur); load(cur, cur);
    return () => { pollRef.current += 1; };
  }, [side]); // eslint-disable-line

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/reports/gst-recon/export?from_month=${from}&to_month=${to}&side=${side}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = side === "input" ? "DeePiping_GSTR2_ITC_vs_GL.xlsx" : "DeePiping_GSTR1_vs_GL.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const monthGroups = {};
  (data?.rows || []).forEach((r) => { (monthGroups[r.period] = monthGroups[r.period] || []).push(r); });

  return (
    <div className="space-y-4" data-testid="gst-recon-panel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Months</span>
        <input type="month" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="gst-recon-from"
          className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50" />
        <span className="text-slate-400 text-xs">to</span>
        <input type="month" value={to} onChange={(e) => setTo(e.target.value)} data-testid="gst-recon-to"
          className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50" />
        <button onClick={() => load()} data-testid="gst-recon-apply"
          className="text-xs bg-[#B01E24] hover:bg-[#8e181d] text-white rounded-md px-3 py-1.5">Apply</button>
        <div className="flex-1" />
        <button onClick={() => load(from, to, true)} disabled={!!building} data-testid="gst-recon-refresh-button"
          title="Re-scan SAP for fresh figures"
          className="flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 bg-white hover:bg-slate-50 rounded-md px-3 py-1.5 disabled:opacity-50">
          <RefreshCw size={12} /> Refresh
        </button>
        <button onClick={exportExcel} disabled={exporting || !data} data-testid="gst-recon-export-button"
          className="flex items-center gap-1.5 text-xs border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-md px-3 py-1.5 disabled:opacity-50">
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Excel
        </button>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="gst-recon-error">{error}</div>}
      {building && (
        <div className="flex items-center gap-2.5 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg p-4" data-testid="gst-recon-building">
          <Loader2 size={16} className="animate-spin text-[#B01E24]" />
          <span>{building}</span>
        </div>
      )}
      {!data && !error && !building && <div className="text-slate-500 text-sm animate-pulse">Reconciling {retLabel} against GL…</div>}

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200" data-testid="gst-recon-tax-source">
              {retLabel} tax: {data.tax_source || "report data"}
            </span>
            <span className={`px-2 py-0.5 rounded-full border ${data.gl_mode === "mock" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid="gst-recon-gl-mode">
              GL side: {data.gl_mode === "configured" ? "mapped GL codes" : data.gl_mode === "auto" ? "auto-detected by GL name" : "mock demo"}
            </span>
            {data.gl_mode !== "configured" && (
              <Link to="/settings" className="flex items-center gap-1 text-[#B01E24] hover:underline" data-testid="gst-recon-settings-link">
                <Settings2 size={11} /> Map exact GL codes in Settings
              </Link>
            )}
            {data.computed_at && (
              <span className="text-slate-400" data-testid="gst-recon-computed-at">
                computed {new Date(data.computed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </span>
            )}
          </div>
          {(data.gl_note || data.tax_note || data.gl_truncated?.length > 0) && (
            <div className="text-[12px] text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 space-y-0.5" data-testid="gst-recon-note">
              {data.gl_note && <div>{data.gl_note}</div>}
              {data.tax_note && <div>{data.tax_note}</div>}
              {data.gl_truncated?.length > 0 && <div>GL scan truncated for {data.gl_truncated.join(", ")} — map exact GL codes in Settings for a complete pull.</div>}
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(data.head_totals || {}).map(([h, t]) => (
              <div key={h} className="rounded-lg border border-slate-200 bg-white shadow-sm p-4" data-testid={`gst-recon-card-${h}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h === "revenue" ? "Revenue vs Taxable" : h.startsWith("itc_") ? `Input ${h.slice(4).toUpperCase()}` : `Output ${h.toUpperCase()}`}
                </div>
                <div className="mt-1 text-lg font-bold text-slate-900">{inr(t.gstr1)}</div>
                <div className="text-[11px] text-slate-500">GL: {inr(t.gl)}</div>
                <div className={`mt-1 text-xs font-semibold ${Math.abs(t.gap) <= 1 ? "text-emerald-600" : "text-red-600"}`}>
                  {Math.abs(t.gap) <= 1 ? "Matched" : `Gap ${inr(t.gap)}`}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white shadow-sm report-scroll">
            <table className="w-full text-sm" data-testid="gst-recon-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="px-4 py-2.5">Month</th>
                  <th className="px-4 py-2.5">Head</th>
                  <th className="px-4 py-2.5 text-right">{retLabel}</th>
                  <th className="px-4 py-2.5 text-right">GL / Trial Balance</th>
                  <th className="px-4 py-2.5 text-right">Gap</th>
                  <th className="px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(monthGroups).map(([period, rows]) =>
                  rows.map((r, i) => (
                    <tr key={`${period}-${r.head}`} onClick={() => setDrill(r)} title="Click to see the GL journal lines behind this figure"
                      className={`${i === 0 ? "border-t border-slate-200" : ""} hover:bg-slate-50 cursor-pointer`} data-testid={`gst-recon-row-${r.month}-${r.head}`}>
                      <td className="px-4 py-2 font-semibold text-slate-800">{i === 0 ? period : ""}</td>
                      <td className={`px-4 py-2 ${HEAD_COLORS[r.head]}`}>{r.head_label}</td>
                      <td className="px-4 py-2 text-right font-mono text-slate-800">{inr(r.gstr1)}</td>
                      <td className="px-4 py-2 text-right font-mono text-slate-800">{inr(r.gl)}</td>
                      <td className={`px-4 py-2 text-right font-mono font-semibold ${Math.abs(r.gap) <= 1 ? "text-slate-400" : "text-red-600"}`}>{inr(r.gap)}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${r.status === "Matched" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{r.status}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {Object.keys(data.gl_accounts || {}).length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
              <button onClick={() => setShowAccts(!showAccts)} data-testid="gst-recon-accounts-toggle" className="text-xs font-semibold text-slate-700 hover:text-[#B01E24]">
                {showAccts ? "Hide" : "Show"} GL accounts counted ({Object.values(data.gl_accounts).reduce((s, a) => s + a.length, 0)})
              </button>
              {showAccts && (
                <div className="mt-3 space-y-2" data-testid="gst-recon-accounts">
                  {Object.entries(data.gl_accounts).map(([h, accts]) => (
                    <div key={h} className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 w-16">{h}</span>
                      {accts.map((a) => (
                        <span key={a.gl} className="font-mono text-[10.5px] bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5" title={a.name}>{a.gl}{a.name ? ` · ${a.name}` : ""}</span>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {drill && (
            <GlLinesDialog month={drill.month} period={drill.period} head={drill.head} headLabel={drill.head_label}
              side={side} onClose={() => setDrill(null)} />
          )}
        </>
      )}
    </div>
  );
};

export default GstGlRecon;
